type PriceRow = {
  packageKey: string;
  fuel: string;
  tonnage: number | null;
  zoneCount: number | null;
  price: number;
  family: "central" | "furnace" | "acOnly" | "miniSplit" | "unknown";
  tier: "good" | "better" | "best" | "other";
  band: "low" | "high" | "single";
  raw: Record<string, any>;
};

type TierOption = {
  title: string;
  tag: string;
  headline: string;
  description: string;
  highlights: string[];
  price: number;
  monthlyPrice: number | null;
  startingPrice: number;
  startingMonthlyPrice: number | null;
  rangeMin: number;
  rangeMax: number;
};

const MONTHLY_TERM_MONTHS = 120;
const MIN_TIER_PRICE_GAP = MONTHLY_TERM_MONTHS;

const PACKAGE_KEYS = ["desiredSystemType", "package", "system", "option"];
const FUEL_KEYS = ["desiredFuelType", "fuelType", "fuel"];
const TONNAGE_KEYS = ["currentACtonnage", "acTonnage", "tonnage"];
const PRICE_KEYS = ["Base Price", "basePrice", "BasePrice", "Price", "price", "Amount", "amount"];

function normalizeKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function normalizeValue(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/["']/g, "")
    .replace(/\s*\/\s*/g, "/")
    .replace(/\s+/g, " ")
    .trim();
}

function toNumber(value: unknown) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  const cleaned = raw.replace(/[$,\s]/g, "");
  const direct = Number(cleaned);
  if (Number.isFinite(direct)) return direct;

  const numericMatch = raw.match(/-?\d+(\.\d+)?/);
  if (!numericMatch) return null;

  const parsed = Number(numericMatch[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function getByAliases(row: Record<string, any>, aliases: string[]) {
  const keyMap = Object.keys(row).reduce(
    (acc, key) => {
      acc[normalizeKey(key)] = key;
      return acc;
    },
    {} as Record<string, string>,
  );

  for (const alias of aliases) {
    const match = keyMap[normalizeKey(alias)];
    if (match) return row[match];
  }
  return undefined;
}

function getPackageFamily(packageKey: string): PriceRow["family"] {
  const v = normalizeValue(packageKey);
  if (v.includes("centralheatairsystem")) return "central";
  if (v.includes("furnaceonly")) return "furnace";
  if (v.includes("aconly")) return "acOnly";
  if (v.includes("minisplit")) return "miniSplit";
  return "unknown";
}

function getTier(packageKey: string): PriceRow["tier"] {
  const v = normalizeValue(packageKey);
  if (v.startsWith("good")) return "good";
  if (v.startsWith("better")) return "better";
  if (v.startsWith("best")) return "best";
  return "other";
}

function getBand(packageKey: string): PriceRow["band"] {
  const v = normalizeValue(packageKey);
  if (v.includes("high") || v.includes("lhigh")) return "high";
  if (v.includes("low")) return "low";
  return "single";
}

function parseZoneCount(value: unknown) {
  const match = String(value ?? "").match(/\d+/);
  return match ? Number(match[0]) : null;
}

function toMonthlyEstimate(basePrice: number) {
  if (!Number.isFinite(basePrice) || basePrice <= 0) return null;
  return Math.round(basePrice / MONTHLY_TERM_MONTHS);
}

function enforceTierPriceOrder(options: TierOption[]) {
  if (options.length < 2) return options;

  const adjusted: TierOption[] = [options[0]];
  for (let i = 1; i < options.length; i++) {
    const option = options[i];
    const previous = adjusted[i - 1];

    const adjustedPrice = Math.max(option.price, previous.price + 1);
    const adjustedStartingPrice = Math.max(
      option.startingPrice,
      previous.startingPrice + MIN_TIER_PRICE_GAP,
    );
    const adjustedMonthlyPrice = toMonthlyEstimate(adjustedStartingPrice);

    adjusted.push({
      ...option,
      price: adjustedPrice,
      startingPrice: adjustedStartingPrice,
      startingMonthlyPrice: adjustedMonthlyPrice,
      monthlyPrice: adjustedMonthlyPrice,
    });
  }

  return adjusted;
}

function extractPriceRows(pricing: any[]): PriceRow[] {
  return pricing
    .map((row: Record<string, any>) => {
      const packageKey = String(getByAliases(row, PACKAGE_KEYS) ?? "").trim();
      const fuel = String(getByAliases(row, FUEL_KEYS) ?? "").trim();
      const tonnage = toNumber(getByAliases(row, TONNAGE_KEYS));
      const price = toNumber(getByAliases(row, PRICE_KEYS));
      const zoneCount = parseZoneCount(fuel);

      if (!packageKey || price === null) return null;

      return {
        packageKey,
        fuel,
        tonnage,
        zoneCount,
        price,
        family: getPackageFamily(packageKey),
        tier: getTier(packageKey),
        band: getBand(packageKey),
        raw: row,
      } as PriceRow;
    })
    .filter((row): row is PriceRow => !!row);
}

function normalizeFuelForMatch(value: unknown) {
  const v = normalizeValue(value);
  if (v.includes("dont know") || v.includes("not sure") || !v) return "unknown";
  if (v.includes("electric/heat pump")) return "electric/heat pump";
  if (v.includes("dual-fuel") || v.includes("dualfuel")) return "dual-fuel";
  if (v.includes("gas")) return "gas";
  return v;
}

function mapLeadFamily(value: unknown): PriceRow["family"] {
  const v = normalizeValue(value);
  if (v.includes("mini")) return "miniSplit";
  if (v.includes("furnace")) return "furnace";
  if (v.includes("central") || v.includes("full system") || v.includes("replacement")) {
    return "central";
  }
  if (v.includes("ac only") || v === "ac") return "acOnly";
  return "unknown";
}

function getBestRowsForLead(lead: any, pricing: any[]) {
  const rows = extractPriceRows(pricing);
  const family = mapLeadFamily(lead?.desiredSystemType);
  const fuel = normalizeFuelForMatch(lead?.desiredFuelType);
  const tonnage = toNumber(lead?.currentACtonnage);
  const rooms = parseZoneCount(lead?.miniSplitRooms);

  let candidates = rows.filter((row) => row.family === family);

  if (family === "miniSplit") {
    if (rooms !== null) {
      const exactZone = candidates.filter((row) => row.zoneCount === rooms);
      if (exactZone.length > 0) return exactZone;

      const nearestZone = [...candidates].sort((a, b) => {
        const az = a.zoneCount ?? 0;
        const bz = b.zoneCount ?? 0;
        return Math.abs(az - rooms) - Math.abs(bz - rooms);
      });
      const nearestCount = nearestZone[0]?.zoneCount;
      if (nearestCount !== undefined) {
        return candidates.filter((row) => row.zoneCount === nearestCount);
      }
    }
    return candidates;
  }

  if (fuel !== "unknown") {
    const fuelRows = candidates.filter(
      (row) => normalizeFuelForMatch(row.fuel) === fuel,
    );
    if (fuelRows.length > 0) candidates = fuelRows;
  }

  if (tonnage !== null) {
    const tonnageRows = candidates.filter((row) => row.tonnage === tonnage);
    if (tonnageRows.length > 0) candidates = tonnageRows;
  }

  return candidates;
}

function buildTierOptions(rows: PriceRow[]) {
  const tierOrder: Array<"good" | "better" | "best"> = ["good", "better", "best"];
  const tierMeta: Record<
    "good" | "better" | "best",
    {
      title: string;
      tag: string;
      headline: string;
      description: string;
      highlights: string[];
    }
  > = {
    good: {
      title: "Good Package",
      tag: "Good Value",
      headline: "Reliable & Budget-Friendly",
      description:
        "Best for homeowners looking for a dependable replacement at the lowest upfront cost.",
      highlights: [
        "Reliable, entry-level equipment",
        "Standard efficiency improvements over your old system",
        "Consistent comfort with straightforward operation",
        "Ideal for tighter budgets or rental properties",
      ],
    },
    better: {
      title: "Better Package",
      tag: "Better Value",
      headline: "Balanced Comfort & Efficiency",
      description: "Best for most homes and long-term value.",
      highlights: [
        "Higher efficiency equipment",
        "Enhanced comfort and temperature control",
        "Quieter operation",
        "Strong balance of upfront cost and long-term savings",
      ],
    },
    best: {
      title: "Best Package",
      tag: "Best Value",
      headline: "Premium Comfort & Performance",
      description:
        "Best for homeowners who want maximum comfort, efficiency, and future-proofing.",
      highlights: [
        "Premium, high-efficiency equipment",
        "Maximum comfort and temperature consistency",
        "Quietest operation",
        "Often paired with advanced features and longer equipment life",
      ],
    },
  };

  const tierOptions = tierOrder
    .map((tier) => {
      const tierRows = rows.filter((row) => row.tier === tier);
      if (tierRows.length === 0) return null;

      const low = tierRows.find((row) => row.band === "low");
      const high = tierRows.find((row) => row.band === "high");
      const prices = tierRows.map((row) => row.price).sort((a, b) => a - b);
      const min = low?.price ?? prices[0];
      const max = high?.price ?? prices[prices.length - 1];
      const estimate = Math.round((min + max) / 2);

      return {
        title: tierMeta[tier].title,
        tag: tierMeta[tier].tag,
        headline: tierMeta[tier].headline,
        description: tierMeta[tier].description,
        highlights: tierMeta[tier].highlights,
        price: estimate,
        monthlyPrice: toMonthlyEstimate(min),
        startingPrice: min,
        startingMonthlyPrice: toMonthlyEstimate(min),
        rangeMin: min,
        rangeMax: max,
      };
    })
    .filter((item): item is NonNullable<typeof item> => !!item);

  return enforceTierPriceOrder(tierOptions);
}

function buildMiniSplitOptions(rows: PriceRow[]) {
  const low = rows.find((row) => row.band === "low");
  const high = rows.find((row) => row.band === "high");
  const prices = rows.map((row) => row.price).sort((a, b) => a - b);

  const min = low?.price ?? prices[0];
  const max = high?.price ?? prices[prices.length - 1];

  return [
    {
      title: "Lower-Cost Estimate",
      tag: "Estimated Range",
      headline: "Efficient comfort for targeted spaces",
      description:
        "This estimate represents a more straightforward mini-split installation with reliable equipment and standard configurations.",
      highlights: [
        "Duct-free comfort in specific rooms or zones",
        "Cost-effective solution with strong performance",
        "Minimal installation complexity",
      ],
      price: min,
      monthlyPrice: toMonthlyEstimate(min),
      startingPrice: min,
      startingMonthlyPrice: toMonthlyEstimate(min),
      rangeMin: min,
      rangeMax: max,
    },
    {
      title: "Upper-Cost Estimate",
      tag: "Estimated Range",
      headline: "Enhanced comfort with added flexibility",
      description:
        "This estimate reflects projects with higher complexity, additional labor, or customization needs.",
      highlights: [
        "Multiple rooms or larger zones covered",
        "Greater layout flexibility",
        "Solutions tailored to harder-to-reach spaces",
      ],
      price: max,
      monthlyPrice: toMonthlyEstimate(max),
      startingPrice: max,
      startingMonthlyPrice: toMonthlyEstimate(max),
      rangeMin: min,
      rangeMax: max,
    },
  ];
}

export function getRecommendedOptions(lead: any, pricing: any[], limit = 3) {
  const bestRows = getBestRowsForLead(lead, pricing);
  if (bestRows.length === 0) return [];

  const family = bestRows[0].family;
  const options =
    family === "miniSplit" ? buildMiniSplitOptions(bestRows) : buildTierOptions(bestRows);

  return options.slice(0, limit);
}

export function resolvePrice(lead: any, pricing: any[]) {
  const options = getRecommendedOptions(lead, pricing, 1);
  if (options.length === 0) return null;
  return { "Base Price": options[0].price };
}
